import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { SessionMediaSettingsDto } from '@/types/dtos/media/media-settings.dto';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import {
  patchSessionMediaSettingsInEventStore,
  patchSessionMediaSettingsInQueryCache,
} from '@/state/mutations/media/patch-media-settings-cache';

export const usePatchSessionMediaSettingsMutation = (options?: { onSuccess?: () => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, mediaSettings }: { sessionId: string; mediaSettings: SessionMediaSettingsDto }) => {
      const token = await getToken();
      return patchSessionMediaSettings(sessionId, mediaSettings, token ?? '');
    },
    onSuccess: async (mediaSettings, variables) => {
      patchSessionMediaSettingsInQueryCache(queryClient, variables.sessionId, mediaSettings);
      patchSessionMediaSettingsInEventStore(variables.sessionId, mediaSettings);
      await queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY, variables.sessionId] });
      options?.onSuccess?.();
    },
  });
};

const patchSessionMediaSettings = async (sessionId: string, mediaSettings: SessionMediaSettingsDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/sessions/${sessionId}/media-settings`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: { mediaSettings }, token });
};
